import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { Profile } from '../pages/profileManage/types';
import { useAuth } from '../context/AuthContext';
import {
  fetchProfiles,
  saveProfile,
  deleteProfile,
  deleteProfiles,
  setCurrentProfile,
  voToProfile,
  type SaveProfileInput,
} from '../api/profile';

interface ProfileContextType {
  profiles: Profile[];
  currentProfile: Profile | null;
  isLoading: boolean;
  updateProfiles: (updater: (prev: Profile[]) => Profile[]) => void;
  setCurrentById: (id: number) => Promise<void>;
  addProfile: (data: Partial<Profile>) => Promise<Profile | null>;
  editProfile: (id: number, data: Partial<Profile>) => Promise<void>;
  removeProfile: (id: number) => Promise<void>;
  removeProfiles: (ids: Set<number>) => Promise<void>;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export function ProfileProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useAuth();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // 写入标记：登录 fetch 拉取期间若已有写操作发生，丢弃该次拉取结果，避免回填过期数据
  const dirtyRef = useRef(false);

  // 登录时拉取服务端命主；登出时清空
  useEffect(() => {
    if (!isLoggedIn) {
      setProfiles([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    dirtyRef.current = false;
    setIsLoading(true);
    fetchProfiles()
      .then((list) => {
        if (cancelled || dirtyRef.current) return;
        setProfiles(list.map((vo) => voToProfile(vo)));
      })
      .catch(() => {
        if (cancelled || dirtyRef.current) return;
        setProfiles([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [isLoggedIn]);

  const updateProfiles = useCallback((updater: (prev: Profile[]) => Profile[]) => {
    setProfiles(prev => updater(prev));
  }, []);

  const setCurrentById = useCallback(async (id: number) => {
    dirtyRef.current = true;
    await setCurrentProfile(id);
    setProfiles(prev => prev.map(p => ({ ...p, isCurrent: p.id === id })));
  }, []);

  const addProfile = useCallback(async (data: Partial<Profile>): Promise<Profile | null> => {
    const input: SaveProfileInput = {
      name: data.name || '',
      gender: (data.gender === 'female' ? 'female' : 'male'),
      tag: data.tag || '自己',
      isCurrent: false,
      avatar: data.avatar || '',
      birthTime: data.birthTime || '',
      birthPlace: data.birthPlace,
    };
    dirtyRef.current = true;
    const saved = await saveProfile(input);
    const profile = voToProfile(saved);
    setProfiles(prev => [...prev, profile]);
    return profile;
  }, []);

  const editProfile = useCallback(async (id: number, data: Partial<Profile>) => {
    const saved = await saveProfile({
      id,
      name: data.name || '',
      gender: (data.gender === 'female' ? 'female' : 'male'),
      tag: data.tag || '自己',
      isCurrent: false,
      avatar: data.avatar || '',
      birthTime: data.birthTime || '',
      birthPlace: data.birthPlace,
    });
    dirtyRef.current = true;
    const updated = voToProfile(saved);
    setProfiles(prev => prev.map(p => (p.id === id ? updated : p)));
  }, []);

  const removeProfile = useCallback(async (id: number) => {
    dirtyRef.current = true;
    await deleteProfile(id);
    setProfiles(prev => prev.filter(p => p.id !== id));
  }, []);

  const removeProfiles = useCallback(async (ids: Set<number>) => {
    dirtyRef.current = true;
    await deleteProfiles(Array.from(ids));
    setProfiles(prev => prev.filter(p => !ids.has(p.id)));
  }, []);

  const currentProfile = profiles.find(p => p.isCurrent) ?? profiles[0] ?? null;

  return (
    <ProfileContext.Provider value={{
      profiles, currentProfile, isLoading,
      updateProfiles, setCurrentById, addProfile, editProfile, removeProfile, removeProfiles,
    }}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useProfile must be used within ProfileProvider');
  return ctx;
}

/** 解析 solar 字符串 "1990年06月15日 12時" → "06月15日" */
export function parseSolarMonthDay(solar?: string): string {
  if (!solar) return '';
  const m = solar.match(/\d+年(\d+月\d+日)/);
  return m ? m[1] : '';
}

const EN_MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/** 解析 solar 字符串，根据语言返回本地化月日格式 */
export function parseSolarMonthDayI18n(solar: string | undefined, lang: string): string {
  if (!solar) return '';
  const m = solar.match(/(\d+)年(\d+)月(\d+)日/);
  if (!m) return '';
  const mo = parseInt(m[2]);
  const dy = parseInt(m[3]);
  if (lang === 'en') return `${EN_MONTHS_SHORT[mo - 1] ?? ''} ${dy}`;
  if (lang === 'ko') return `${mo}월 ${dy}일`;
  // zh-CN, zh-HK, ja 都用 月日 格式
  return `${String(mo).padStart(2, '0')}月${String(dy).padStart(2, '0')}日`;
}

// 农历时辰中文→多语言映射
const SHICHEN_I18N: Record<string, Record<string, string>> = {
  '子時': { en: '23:00–01:00', ko: '자시', ja: '子の刻' },
  '丑時': { en: '01:00–03:00', ko: '축시', ja: '丑の刻' },
  '寅時': { en: '03:00–05:00', ko: '인시', ja: '寅の刻' },
  '卯時': { en: '05:00–07:00', ko: '묘시', ja: '卯の刻' },
  '辰時': { en: '07:00–09:00', ko: '진시', ja: '辰の刻' },
  '巳時': { en: '09:00–11:00', ko: '사시', ja: '巳の刻' },
  '午時': { en: '11:00–13:00', ko: '오시', ja: '午の刻' },
  '未時': { en: '13:00–15:00', ko: '미시', ja: '未の刻' },
  '申時': { en: '15:00–17:00', ko: '신시', ja: '申の刻' },
  '酉時': { en: '17:00–19:00', ko: '유시', ja: '酉の刻' },
  '戌時': { en: '19:00–21:00', ko: '술시', ja: '戌の刻' },
  '亥時': { en: '21:00–23:00', ko: '해시', ja: '亥の刻' },
};

// 农历月份中文→多语言
const LUNAR_MONTH_I18N: Record<string, Record<string, string>> = {
  '正月': { en: 'Month 1', ko: '1월', ja: '睦月' },
  '二月': { en: 'Month 2', ko: '2월', ja: '如月' },
  '三月': { en: 'Month 3', ko: '3월', ja: '弥生' },
  '四月': { en: 'Month 4', ko: '4월', ja: '卯月' },
  '五月': { en: 'Month 5', ko: '5월', ja: '皐月' },
  '六月': { en: 'Month 6', ko: '6월', ja: '水無月' },
  '七月': { en: 'Month 7', ko: '7월', ja: '文月' },
  '八月': { en: 'Month 8', ko: '8월', ja: '葉月' },
  '九月': { en: 'Month 9', ko: '9월', ja: '長月' },
  '十月': { en: 'Month 10', ko: '10월', ja: '神無月' },
  '十一月': { en: 'Month 11', ko: '11월', ja: '霜月' },
  '腊月': { en: 'Month 12', ko: '12월', ja: '師走' },
  '臘月': { en: 'Month 12', ko: '12월', ja: '師走' },
};

// 农历日期中文→数字
const LUNAR_DAY_MAP: Record<string, number> = {
  '初一':1,'初二':2,'初三':3,'初四':4,'初五':5,'初六':6,'初七':7,'初八':8,'初九':9,'初十':10,
  '十一':11,'十二':12,'十三':13,'十四':14,'十五':15,'十六':16,'十七':17,'十八':18,'十九':19,'二十':20,
  '廿一':21,'廿二':22,'廿三':23,'廿四':24,'廿五':25,'廿六':26,'廿七':27,'廿八':28,'廿九':29,'三十':30,
};

/**
 * 将农历字符串（如 "己巳年臘月初五午時"）转换为目标语言格式
 * zh-CN/zh-HK 原样返回（繁简转换由其他函数处理）
 */
export function convertLunarDateI18n(lunar: string | undefined, lang: string): string {
  if (!lunar) return '';
  if (lang === 'zh-CN' || lang === 'zh-HK') return lunar;

  // 解析：年干支 + 月 + 日 + 时辰
  const yearMatch = lunar.match(/^([\u4e00-\u9fa5]{2}年)/);
  const year = yearMatch ? yearMatch[1] : '';
  const rest = year ? lunar.slice(year.length) : lunar;

  // 找月份
  let monthStr = '';
  let monthKey = '';
  for (const key of Object.keys(LUNAR_MONTH_I18N)) {
    if (rest.includes(key)) { monthKey = key; monthStr = key; break; }
  }
  const afterMonth = monthStr ? rest.slice(rest.indexOf(monthStr) + monthStr.length) : rest;

  // 找日
  let dayNum = 0;
  let dayKey = '';
  for (const [k, v] of Object.entries(LUNAR_DAY_MAP)) {
    if (afterMonth.startsWith(k)) { dayNum = v; dayKey = k; break; }
  }
  const afterDay = dayKey ? afterMonth.slice(dayKey.length) : afterMonth;

  // 找时辰
  let shichenKey = '';
  for (const key of Object.keys(SHICHEN_I18N)) {
    if (afterDay.includes(key)) { shichenKey = key; break; }
  }

  if (lang === 'en') {
    const mo = LUNAR_MONTH_I18N[monthKey]?.en ?? monthKey;
    const dy = dayNum ? `Day ${dayNum}` : '';
    const sc = shichenKey ? `(${SHICHEN_I18N[shichenKey].en})` : '';
    return [year, mo, dy, sc].filter(Boolean).join(' ');
  }
  if (lang === 'ko') {
    const mo = LUNAR_MONTH_I18N[monthKey]?.ko ?? monthKey;
    const dy = dayNum ? `${dayNum}일` : '';
    const sc = shichenKey ? SHICHEN_I18N[shichenKey].ko : '';
    return [year, mo, dy, sc].filter(Boolean).join(' ');
  }
  if (lang === 'ja') {
    const mo = LUNAR_MONTH_I18N[monthKey]?.ja ?? monthKey;
    const dy = dayNum ? `${dayNum}日` : '';
    const sc = shichenKey ? SHICHEN_I18N[shichenKey].ja : '';
    return [year, mo, dy, sc].filter(Boolean).join('');
  }
  return lunar;
}

/**
 * 将公历字符串（如 "1996年09月02日 10:03時"）转换为目标语言格式
 */
export function convertSolarDateI18n(solar: string | undefined, lang: string): string {
  if (!solar) return '';
  if (lang === 'zh-CN' || lang === 'zh-HK') return solar;
  const m = solar.match(/(\d+)年(\d+)月(\d+)日\s*(\d+:\d+)/);
  if (!m) return solar;
  const [, yr, mo, dy, tm] = m;
  if (lang === 'en') return `${yr}/${mo}/${dy} ${tm}`;
  if (lang === 'ko') return `${yr}년 ${mo}월 ${dy}일 ${tm}`;
  if (lang === 'ja') return `${yr}年${mo}月${dy}日 ${tm}`;
  return solar;
}

/** 解析 solar 字符串获取年份 "1990年06月15日 12時" → "1990" */
export function parseSolarYear(solar?: string): string {
  if (!solar) return '';
  const m = solar.match(/(\d+)年/);
  return m ? m[1] : '';
}

const EN_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/**
 * 根据语言格式化出生日期显示（仅月日）
 * birthTime: ISO格式 "1990-06-15 12:00"
 */
export function formatBirthDisplay(birthTime: string | undefined, lang: string): string {
  if (!birthTime) return '';
  const datePart = birthTime.split(' ')[0];
  const parts = datePart.split('-');
  const mo = parseInt(parts[1] || '1');
  const dy = parseInt(parts[2] || '1');
  if (lang === 'en') return `${EN_MONTHS[mo - 1] ?? ''} ${dy}`;
  if (lang === 'ko') return `${mo}월 ${dy}일`;
  if (lang === 'ja') return `${mo}月${dy}日`;
  return `${String(mo).padStart(2, '0')}月${String(dy).padStart(2, '0')}日`;
}

const ZODIAC_EN: Record<string, string> = {
  '鼠': 'Rat', '牛': 'Ox', '虎': 'Tiger', '兔': 'Rabbit', '龙': 'Dragon', '蛇': 'Snake',
  '马': 'Horse', '羊': 'Goat', '猴': 'Monkey', '鸡': 'Rooster', '狗': 'Dog', '猪': 'Pig',
};
const ZODIAC_KO: Record<string, string> = {
  '鼠': '쥐', '牛': '소', '虎': '호랑이', '兔': '토끼', '龙': '용', '蛇': '뱀',
  '马': '말', '羊': '양', '猴': '원숭이', '鸡': '닭', '狗': '개', '猪': '돼지',
};
const ZODIAC_JA: Record<string, string> = {
  '鼠': '子', '牛': '丑', '虎': '寅', '兔': '卯', '龙': '辰', '蛇': '巳',
  '马': '午', '羊': '未', '猴': '申', '鸡': '酉', '狗': '戌', '猪': '亥',
};

/** 根据语言格式化生肖文字 */
export function formatZodiac(zodiac: string, lang: string): string {
  if (lang === 'en') return ZODIAC_EN[zodiac] ?? zodiac;
  if (lang === 'ko') return ZODIAC_KO[zodiac] ?? zodiac;
  if (lang === 'ja') return ZODIAC_JA[zodiac] ?? zodiac;
  return zodiac;
}
